import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import { SlackProvider } from "@/contexts/SlackContext";
import { PermissionProvider, usePermissions } from "@/contexts/PermissionContext";
import { FloatingButtons } from "@/components/FloatingButtons";
import { PageLoader } from "@/components/PageLoader";

const LoginPage = lazy(() => import("@/pages/LoginPage"));
const DashboardPage = lazy(() => import("@/pages/DashboardPage"));
const DevelopersPage = lazy(() => import("@/pages/DevelopersPage"));
const ProjectsPage = lazy(() => import("@/pages/ProjectsPage"));
const ProjectDetailPage = lazy(() => import("@/pages/ProjectDetailPage"));
const SprintsPage = lazy(() => import("@/pages/SprintsPage"));
const SprintDetailPage = lazy(() => import("@/pages/SprintDetailPage"));
const SprintBuildPage = lazy(() => import("@/pages/SprintBuildPage"));
const SprintTasksPage = lazy(() => import("@/pages/SprintTasksPage"));
const TestCasesPage = lazy(() => import("@/pages/TestCasesPage"));
const TestCaseUploadPage = lazy(() => import("@/pages/TestCaseUploadPage"));
const AdminSettingsPage = lazy(() => import("@/pages/AdminSettingsPage"));
const UserManagementPage = lazy(() => import("@/pages/UserManagementPage"));
const ChatModerationPage = lazy(() => import("@/pages/ChatModerationPage"));
const AiUsagePage = lazy(() => import("@/pages/AiUsagePage"));
const BugsPage = lazy(() => import("@/pages/BugsPage"));
const StandUpPage = lazy(() => import("@/pages/StandUpPage"));
const DailySchedulePage = lazy(() => import("@/pages/DailySchedulePage"));
const ClientEnquiryPage = lazy(() => import("@/pages/ClientEnquiryPage"));
const PerformancePage = lazy(() => import("@/pages/PerformancePage"));
const SprintReportsPage = lazy(() => import("@/pages/SprintReportsPage"));
const TaskBoardPage = lazy(() => import("@/pages/TaskBoardPage"));
const SprintGroupsPage = lazy(() => import("@/pages/SprintGroupsPage"));
const ActivityLogsPage = lazy(() => import("@/pages/ActivityLogsPage"));
const OnBoardPage = lazy(() => import("@/pages/OnBoardPage"));
const SlackChannelPage = lazy(() => import("@/pages/SlackChannelPage"));
const PermissionsPage = lazy(() => import("@/pages/PermissionsPage"));
const EmailDashboardPage = lazy(() => import("@/pages/EmailDashboardPage"));
const AutomationsPage = lazy(() => import("@/pages/AutomationsPage"));
const BugUploadPage = lazy(() => import("@/pages/BugUploadPage"));
const BugReportPage = lazy(() => import("@/pages/BugReportPage"));
const ClientDocAnalyzerPage = lazy(() => import("@/pages/ClientDocAnalyzerPage"));
const PublicTaskPage = lazy(() => import("@/pages/PublicTaskPage"));
const PublicBugPage = lazy(() => import("@/pages/PublicBugPage"));
const StoryBoardPage = lazy(() => import("@/pages/StoryBoardPage"));

import "@/App.css";

function ProtectedRoute({ children, page }) {
  const { user, loading } = useAuth();
  const { hasPerm } = usePermissions();
  if (loading) return <PageLoader />;
  if (!user) return <Navigate to="/login" replace />;
  if (page && !hasPerm(page, "view")) return <Navigate to="/dashboard" replace />;
  return children;
}

function AppRoutes() {
  const { user, loading } = useAuth();
  if (loading) return <PageLoader />;
  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        <Route path="/login" element={user ? <Navigate to="/dashboard" replace /> : <LoginPage />} />
        <Route path="/dashboard" element={<ProtectedRoute page="dashboard"><DashboardPage /></ProtectedRoute>} />
        <Route path="/projects" element={<ProtectedRoute page="projects"><ProjectsPage /></ProtectedRoute>} />
        <Route path="/projects/:id" element={<ProtectedRoute page="projects"><ProjectDetailPage /></ProtectedRoute>} />
        <Route path="/developers" element={<ProtectedRoute page="developers"><DevelopersPage /></ProtectedRoute>} />
        <Route path="/sprints" element={<ProtectedRoute page="sprints"><SprintsPage /></ProtectedRoute>} />
        <Route path="/sprints/build" element={<ProtectedRoute page="sprints"><SprintBuildPage /></ProtectedRoute>} />
        <Route path="/sprints/:id" element={<ProtectedRoute page="sprints"><SprintDetailPage /></ProtectedRoute>} />
        <Route path="/sprints/:id/tasks" element={<ProtectedRoute page="tasks"><SprintTasksPage /></ProtectedRoute>} />
        <Route path="/sprints/:id/tests" element={<ProtectedRoute page="tasks"><TestCasesPage /></ProtectedRoute>} />
        <Route path="/sprints/:id/tests/upload" element={<ProtectedRoute page="tasks"><TestCaseUploadPage /></ProtectedRoute>} />
        <Route path="/sprints/:sprintId/story-board/:storyId" element={<ProtectedRoute page="tasks"><StoryBoardPage /></ProtectedRoute>} />
        <Route path="/admin/settings" element={<ProtectedRoute page="admin_settings"><AdminSettingsPage /></ProtectedRoute>} />
        <Route path="/admin/users" element={<ProtectedRoute page="user_management"><UserManagementPage /></ProtectedRoute>} />
        <Route path="/admin/chat-moderation" element={<ProtectedRoute page="chat_moderation"><ChatModerationPage /></ProtectedRoute>} />
        <Route path="/admin/ai-usage" element={<ProtectedRoute page="ai_usage"><AiUsagePage /></ProtectedRoute>} />
        <Route path="/bugs" element={<ProtectedRoute page="bugs"><BugsPage /></ProtectedRoute>} />
        <Route path="/bugs/upload" element={<ProtectedRoute page="bugs"><BugUploadPage /></ProtectedRoute>} />
        <Route path="/bugs/report" element={<ProtectedRoute page="bugs"><BugReportPage /></ProtectedRoute>} />
        <Route path="/standup" element={<ProtectedRoute page="standup"><StandUpPage /></ProtectedRoute>} />
        <Route path="/daily-schedule" element={<ProtectedRoute page="daily_schedule"><DailySchedulePage /></ProtectedRoute>} />
        <Route path="/performance" element={<ProtectedRoute page="performance"><PerformancePage /></ProtectedRoute>} />
        <Route path="/task-board" element={<ProtectedRoute page="task_board"><TaskBoardPage /></ProtectedRoute>} />
        <Route path="/client-enquiries" element={<ProtectedRoute page="client_enquiries"><ClientEnquiryPage /></ProtectedRoute>} />
        <Route path="/sprint-reports" element={<ProtectedRoute page="sprint_reports"><SprintReportsPage /></ProtectedRoute>} />
        <Route path="/sprint-groups" element={<ProtectedRoute page="sprint_groups"><SprintGroupsPage /></ProtectedRoute>} />
        <Route path="/activity-logs" element={<ProtectedRoute page="activity_logs"><ActivityLogsPage /></ProtectedRoute>} />
        <Route path="/on-board" element={<ProtectedRoute page="on_board"><OnBoardPage /></ProtectedRoute>} />
        <Route path="/slack" element={<ProtectedRoute page="slack"><SlackChannelPage /></ProtectedRoute>} />
        <Route path="/admin/permissions" element={<ProtectedRoute page="admin_settings"><PermissionsPage /></ProtectedRoute>} />
        <Route path="/admin/email-dashboard" element={<ProtectedRoute page="email_dashboard"><EmailDashboardPage /></ProtectedRoute>} />
        <Route path="/admin/automations" element={<ProtectedRoute page="automations"><AutomationsPage /></ProtectedRoute>} />
        <Route path="/public/task/:seqId" element={<PublicTaskPage />} />
        <Route path="/public/bug/:bugNumber" element={<PublicBugPage />} />
        <Route path="/doc-analyzer" element={<ProtectedRoute page="doc_analyzer"><ClientDocAnalyzerPage /></ProtectedRoute>} />
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <AuthProvider>
      <PermissionProvider>
        <SlackProvider>
          <BrowserRouter>
            <AppRoutes />
            <FloatingButtons />
          </BrowserRouter>
        </SlackProvider>
      </PermissionProvider>
      <Toaster position="top-right" richColors />
    </AuthProvider>
  );
}

export default App;
